import { useEffect, useState } from "react"
import { Platform } from "react-native"

import axios from "axios"
import { URL } from "react-native-url-polyfill"

export interface WebsiteThumbnailData {
  /** Page title extracted from <title> / OG metadata (empty string if unavailable). */
  title: string
  /** Resolved Open Graph / scraped image URL, or null when none could be found. */
  imageUrl: string | null
}

export interface UseWebsiteThumbnailResult extends WebsiteThumbnailData {
  loading: boolean
  error: boolean
}

// Module-level cache keyed by URL. The content library grid recycles cards as the
// user scrolls and the chat history can render the same link repeatedly, so caching
// avoids redundant network requests (and respects Microlink rate limits on web).
const cache = new Map<string, WebsiteThumbnailData>()

const SCRAPE_HEADERS = {
  // Browser-like User-Agent to avoid 403 blocks from sites like NYTimes.
  "User-Agent":
    "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36",
  Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8",
  "Accept-Language": "en-US,en;q=0.5",
}

// Meta tag patterns tried in order of preference when scraping HTML on native.
const IMAGE_PATTERNS = [
  /<meta[^>]*itemprop="image"[^>]*content="([^"]*)"/, // Schema.org
  /<meta[^>]*property="og:image"[^>]*content="([^"]*)"/, // Open Graph
  /<meta[^>]*name="twitter:image"[^>]*content="([^"]*)"/, // Twitter Cards
  /<img[^>]*src="([^"]*)"/, // Fallback to first image
]

async function fetchWebsiteThumbnail(websiteUrl: string): Promise<WebsiteThumbnailData> {
  // On web, use the Microlink API to extract OG metadata server-side (bypasses CORS).
  if (Platform.OS === "web") {
    const encoded = encodeURIComponent(websiteUrl)
    const response = await axios.get(`https://api.microlink.io?url=${encoded}`, { timeout: 10000 })
    const data = response.data?.data
    return {
      title: data?.title ?? "",
      imageUrl: data?.image?.url ?? data?.logo?.url ?? null,
    }
  }

  // On native, scrape the HTML directly for title and image meta tags.
  const response = await axios.get(websiteUrl, {
    headers: SCRAPE_HEADERS,
    timeout: 10000,
    // Do not send cookies to third-party sites; avoids Android OkHttp malformed-cookie crash.
    withCredentials: false,
  })
  const html: string = response.data
  const titleMatch = html.match(/<title>(.*?)<\/title>/)
  const title = titleMatch ? titleMatch[1] : ""

  let imageUrl: string | null = null
  for (const pattern of IMAGE_PATTERNS) {
    const match = html.match(pattern)
    if (match && match[1]) {
      imageUrl = new URL(match[1], websiteUrl).toString()
      break
    }
  }
  return { title, imageUrl }
}

/**
 * Fetches a thumbnail image (and title) for an arbitrary website URL by extracting
 * Open Graph / meta-image data. Shared by the chat-history website preview and the
 * content library card fallback.
 *
 * Pass `null`/`undefined` to disable the fetch (the hook is still called unconditionally,
 * so callers can gate it without violating the rules of hooks).
 */
export function useWebsiteThumbnail(
  websiteUrl: string | null | undefined,
): UseWebsiteThumbnailResult {
  const [data, setData] = useState<WebsiteThumbnailData | null>(() =>
    websiteUrl ? (cache.get(websiteUrl) ?? null) : null,
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!websiteUrl) {
      setData(null)
      setLoading(false)
      setError(false)
      return
    }

    const cached = cache.get(websiteUrl)
    if (cached) {
      setData(cached)
      setLoading(false)
      setError(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(false)

    fetchWebsiteThumbnail(websiteUrl)
      .then((result) => {
        cache.set(websiteUrl, result)
        if (cancelled) return
        setData(result)
        setLoading(false)
      })
      .catch((err) => {
        console.warn(`Failed to fetch website thumbnail for ${websiteUrl}: ${err}`)
        if (cancelled) return
        setData({ title: "", imageUrl: null })
        setLoading(false)
        setError(true)
      })

    return () => {
      cancelled = true
    }
  }, [websiteUrl])

  return {
    title: data?.title ?? "",
    imageUrl: data?.imageUrl ?? null,
    loading,
    error,
  }
}
